import { toWebMcpTool } from '@clawharbor/manifest';
import type { Capability, Manifest, WebMcpToolResult } from '@clawharbor/manifest';
import type { CapabilityResult, FreshnessState } from '@clawharbor/render-core';

/**
 * THE ONLY FILE IN THE REPO THAT TOUCHES THE WebMCP API.
 *
 * WebMCP is a Community Group draft. Its surface has already moved once
 * (`navigator.modelContext` -> `document.modelContext`) and it has already had a
 * method deleted (`requestUserInteraction`). Everything spec-fragile therefore
 * lives here and nowhere else, so the next breaking change is a one-file edit.
 *
 * Facts this file depends on. Every one of them was READ OFF A RUNNING BROWSER
 * on 2026-08-28 — Chrome 151.0.7922.175 and Chromium 151.0.7922.34 with
 * `--enable-features=WebMCPTesting` — by `tools/webmcp-check`. That tool is not
 * part of `npm test` (it needs a browser and about 40 seconds); run it by hand
 * before trusting any line below, and it exits non-zero the moment one of them
 * stops being true:
 *
 *   node --import tsx tools/webmcp-check/src/cli.ts
 *
 *  - `document.modelContext`; `navigator.modelContext` is the SAME object and
 *    logs a deprecation warning when read.
 *  - It exists ONLY in a secure context. On plain http it is `undefined` even
 *    with the flag on, which is why `detectModelContext()` returning `none` must
 *    never be narrated as "this browser is too old".
 *  - registerTool(descriptor, { signal }) returns a promise and that signal is
 *    the ONLY teardown mechanism. There is no unregisterTool(); the prototype is
 *    exactly registerTool / getTools / executeTool / ontoolchange.
 *  - registerTool rejects PER TOOL: InvalidStateError on a duplicate live name,
 *    AbortError on an already-aborted signal. One bad descriptor must not cost
 *    the page the other three, so the loop below catches individually.
 *  - ToolAnnotations is exactly { readOnlyHint, untrustedContentHint }, proven by
 *    round-trip: destructiveHint / openWorldHint / idempotentHint are accepted
 *    and then silently dropped. `toWebMcpTool()` owns that mapping in one place.
 *  - execute(input) is called with ONE argument. No options, no per-call signal.
 *    It may return ANY JSON-serialisable value; the user agent JSON.stringify()s
 *    it, and `executeTool()` resolves with that STRING. We return plain
 *    structured JSON — never an MCP `{ content: [...] }` block, which only
 *    carries meaning through a bridge.
 *  - The user agent does NOT validate arguments against inputSchema.
 *  - A throw or rejection from execute() reaches the caller as an opaque
 *    `UnknownError: Tool was executed but the invocation failed…`. The reason is
 *    lost. Hence: this file returns a structured failure and never throws.
 *  - WebMCP cannot return UI. The page repaint IS the result, which is the
 *    spec's stated design intent ("prevent web content disintermediation"), so
 *    every execute() below writes to the page before it returns.
 */

export type ModelContextSource = 'document' | 'navigator' | 'none';

export interface ModelContextDetection {
  mc: WebMcpModelContext | null;
  source: ModelContextSource;
}

/**
 * Feature detection, done once and honestly.
 *
 * If this returns `none` the page must still be a fully working website. We do
 * not polyfill and we do not pretend: the debug strip says agent tools are
 * unavailable and every control keeps working for the human.
 */
export function detectModelContext(): ModelContextDetection {
  if (typeof document === 'undefined') return { mc: null, source: 'none' };

  const fromDocument = document.modelContext;
  if (fromDocument && typeof fromDocument.registerTool === 'function') {
    return { mc: fromDocument, source: 'document' };
  }

  const fromNavigator = typeof navigator !== 'undefined' ? navigator.modelContext : undefined;
  if (fromNavigator && typeof fromNavigator.registerTool === 'function') {
    return { mc: fromNavigator, source: 'navigator' };
  }

  return { mc: null, source: 'none' };
}

/** A `stage` capability's result. Extends the shared envelope; still plain JSON. */
export interface WebMcpStageResult extends WebMcpToolResult {
  staged: true;
  /**
   * Always true. ClawHarbor never completes a transaction on a third party's
   * behalf: the agent has proposed, and only a human press finishes the job on
   * the operator's own checkout. WebMCP deleted `requestUserInteraction`, so
   * this flag plus the on-page panel IS the human-in-the-loop mechanism.
   */
  awaitingUserConfirmation: true;
}

/** What the tool layer produced, before it is squeezed into the wire envelope. */
export interface HostOutcome {
  result: CapabilityResult;
  deepLink?: string;
  summary: string;
}

/**
 * What the app must provide for a tool to be able to do anything.
 *
 * Deliberately narrow: this file knows how to talk to a user agent and nothing
 * at all about fetching, rendering or state shape. The host implementation
 * writes to the page BEFORE resolving, which is what makes the repaint and the
 * spoken answer land together.
 */
export interface WebMcpHost {
  run(cap: Capability, input: Record<string, unknown>, signal: AbortSignal): Promise<HostOutcome>;
}

export interface Registration {
  source: ModelContextSource;
  available: boolean;
  /** Tool names the user agent actually accepted. */
  toolNames: string[];
  errors: string[];
  /** Aborts the registration signal — the spec's only teardown path. */
  dispose(): void;
}

/**
 * Cap on rows handed back to the agent.
 *
 * The page holds all of them; the agent gets a taste. A tool result is context
 * the model pays for on every subsequent turn, and the full set is one glance
 * away on the surface that is already in front of the user.
 */
const MAX_ITEMS_TO_AGENT = 20;

/**
 * The manifest's wire envelope has three freshness states; render-core models
 * five. Collapsing here rather than widening the shared type keeps the contract
 * that BOTH surfaces emit identical, and the precise wording survives in `note`.
 */
function toWireFreshness(state: FreshnessState): 'live' | 'stale' | 'fixture' {
  switch (state) {
    case 'live':
    case 'cached':
      // A cache hit inside its TTL is what "live" means operationally here;
      // `normalizeFreshness` has already demoted an expired hit to `stale`.
      return 'live';
    case 'fixture':
      return 'fixture';
    default:
      // `unknown` included: an unlabelled answer must never be narrated as live.
      return 'stale';
  }
}

/**
 * Builds the AbortSignal handed to ONE execution, chained off every upstream
 * signal that can legitimately cancel it.
 *
 * MEASURED, Chrome 151 (`tools/webmcp-check`): the user agent calls
 * `execute(input)` with `arguments.length === 1`. There is no options object and
 * therefore no per-call signal — so `hostSignal` is, today, always undefined and
 * the registration signal is the ONLY cancellation this page will ever receive.
 * It is still accepted first, because a later Chrome may start supplying one.
 *
 * Chaining the REGISTRATION signal in is not defensive tidiness, it fixes an
 * observed bug. `getTools()` empties the moment that signal aborts and the
 * user agent immediately rejects the in-flight `executeTool()` — but the
 * page-side promise kept running, resolved ~2s later, and repainted the store
 * with a result no agent ever received, attributed to an agent, for a tool that
 * no longer existed. On `boot.ts`'s reload path that is a stale manifest's call
 * painting over the manifest that replaced it.
 *
 * A fresh controller per execution rather than passing an upstream signal
 * straight through: one cancelled call must never cancel its siblings.
 */
function perExecutionSignal(...upstream: Array<AbortSignal | undefined>): {
  signal: AbortSignal;
  release: () => void;
} {
  const exec = new AbortController();
  const live = upstream.filter((s): s is AbortSignal => s != null);

  const alreadyAborted = live.find((s) => s.aborted);
  if (alreadyAborted) {
    exec.abort(alreadyAborted.reason);
    return { signal: exec.signal, release: () => {} };
  }

  const onAbort = (event: Event): void => {
    exec.abort((event.target as AbortSignal | null)?.reason);
  };
  for (const s of live) s.addEventListener('abort', onAbort, { once: true });

  return {
    signal: exec.signal,
    release: () => {
      for (const s of live) s.removeEventListener('abort', onAbort);
    },
  };
}

function buildResult(cap: Capability, outcome: HostOutcome): WebMcpToolResult | WebMcpStageResult {
  const { result } = outcome;
  const total = result.total ?? result.rows.length;

  const payload: WebMcpToolResult = {
    ok: true,
    view: cap.view,
    count: result.rows.length,
    summary: outcome.summary,
    freshness: {
      state: toWireFreshness(result.freshness.state),
      asOf: result.freshness.fetchedAt ?? new Date().toISOString(),
    },
    items: result.rows.slice(0, MAX_ITEMS_TO_AGENT),
  };
  if (outcome.deepLink) payload.deepLink = outcome.deepLink;

  const notes = [...result.notes];
  if (result.freshness.state === 'stale' || result.freshness.state === 'unknown') {
    notes.push(`freshness is ${result.freshness.state}; say so before quoting a time`);
  }
  if (total > MAX_ITEMS_TO_AGENT) {
    notes.push(`returned ${MAX_ITEMS_TO_AGENT} of ${total}; all of them are already on the page`);
  }
  if (notes.length) payload.note = notes.join('; ');

  if (cap.kind === 'stage') {
    return { ...payload, staged: true, awaitingUserConfirmation: true };
  }
  return payload;
}

/**
 * Registers every capability whose manifest says it belongs on this surface.
 *
 * Failures are per-tool: one descriptor the user agent rejects must not cost the
 * page the other three.
 */
export async function registerCapabilities(
  manifest: Manifest,
  host: WebMcpHost,
): Promise<Registration> {
  const { mc, source } = detectModelContext();
  const registrationController = new AbortController();
  const toolNames: string[] = [];
  const errors: string[] = [];

  const registration: Registration = {
    source,
    available: mc != null,
    toolNames,
    errors,
    dispose: () => registrationController.abort(),
  };

  if (!mc) return registration;

  for (const cap of manifest.capabilities.filter((c) => c.surfaces.web.register)) {
    const descriptor = toWebMcpTool(cap);

    const init: WebMcpToolInit = {
      name: descriptor.name,
      title: descriptor.title,
      description: descriptor.description,
      inputSchema: descriptor.inputSchema,
      annotations: descriptor.annotations,

      async execute(rawInput: unknown, options?: WebMcpToolExecuteOptions): Promise<unknown> {
        // The user agent does NOT validate `rawInput` against `inputSchema` —
        // wrong types, missing `required` members and extra properties all
        // arrive here untouched (measured). Everything downstream must treat it
        // as agent-supplied, not schema-checked.
        const input =
          rawInput && typeof rawInput === 'object' ? (rawInput as Record<string, unknown>) : {};
        const { signal, release } = perExecutionSignal(
          options?.signal,
          registrationController.signal,
        );

        try {
          // host.run() repaints the page before it resolves, so by the time the
          // agent narrates the answer the human is already looking at it.
          const outcome = await host.run(cap, input, signal);
          return buildResult(cap, outcome);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);

          // A thrown error becomes an opaque host-level failure the model cannot
          // reason about. A structured failure lets it explain itself and retry.
          const failure: WebMcpToolResult = {
            ok: false,
            view: cap.view,
            count: 0,
            summary: `${cap.title} failed: ${message}`,
            freshness: { state: 'stale', asOf: new Date().toISOString() },
            items: [],
            note: signal.aborted
              ? 'the page tore this tool down while the call was in flight; nothing on the page changed'
              : 'the data plane or the origin did not answer; nothing on the page changed',
          };
          return failure;
        } finally {
          release();
        }
      },
    };

    try {
      // `{ signal }` is the entire teardown story — there is no unregisterTool().
      await mc.registerTool(init, { signal: registrationController.signal });
      toolNames.push(cap.name);
    } catch (err) {
      // Per-tool on purpose. The two rejections a correct manifest can still
      // provoke are both other people's doing: InvalidStateError when something
      // else on the page already owns this name, AbortError when the page tore
      // the registration down mid-loop. Neither justifies dropping the rest.
      const name = err instanceof Error ? err.name : '';
      const message = err instanceof Error ? err.message : String(err);
      const why =
        name === 'InvalidStateError'
          ? `${message} — another script on this page already registered "${cap.name}"`
          : name === 'AbortError'
            ? `${message} — the page tore this registration down mid-loop`
            : message;
      errors.push(`${cap.name}: ${why}`);
    }
  }

  return registration;
}

/**
 * Subscribes to `toolchange`, the spec's one notification event.
 *
 * Feeds the debug strip so it shows the tool set the USER AGENT believes in,
 * rather than the one we hoped it accepted.
 */
export function observeToolChanges(onChange: (names: string[]) => void): () => void {
  const { mc } = detectModelContext();
  if (!mc || typeof mc.getTools !== 'function') return () => {};

  const handler = (): void => {
    void Promise.resolve(mc.getTools?.())
      .then((tools) => onChange((tools ?? []).map((t) => t.name)))
      .catch(() => {});
  };

  mc.addEventListener('toolchange', handler);
  handler();
  return () => mc.removeEventListener('toolchange', handler);
}
